import type { WireArtifact, WireCitation } from '@logistics/contracts';
import { kindLabel, shortDay, weekday } from '../../lib/format';
import { sourceKindName } from '../../lib/glyphs';

/** The design's names and marks for each kind of artifact (Agent Control Panel, ART_KINDS). */
const KINDS: Readonly<Record<WireArtifact['kind'], { glyph: string; label: string }>> = {
  load: { glyph: 'LD', label: 'Load card' },
  map: { glyph: 'MAP', label: 'Live location' },
  hos: { glyph: 'HOS', label: 'HOS clocks' },
  table: { glyph: 'TBL', label: 'Table' },
  doc: { glyph: 'PDF', label: 'Document' },
  email: { glyph: 'EML', label: 'Email thread' },
  chart: { glyph: 'CHT', label: 'Chart' },
  timeline: { glyph: 'RTE', label: 'Route timeline' },
  money: { glyph: 'RATE', label: 'Rates' },
  board: { glyph: 'BRD', label: 'Load board' },
  customers: { glyph: 'CUS', label: 'Customers' },
  calendar: { glyph: 'CAL', label: 'Calendar' },
  ranking: { glyph: 'TOP', label: 'Ranking' },
  kpi: { glyph: 'KPI', label: 'Headline numbers' },
  chat: { glyph: 'CHT', label: 'Chat thread' },
  vehicle: { glyph: 'TRK', label: 'Truck' },
  driver: { glyph: 'DRV', label: 'Driver' },
};

export function artifactGlyph(artifact: WireArtifact): string {
  return KINDS[artifact.kind].glyph;
}

export function artifactKindLabel(artifact: WireArtifact): string {
  return KINDS[artifact.kind].label;
}

type Title<K extends WireArtifact['kind']> = (
  artifact: Extract<WireArtifact, { kind: K }>,
) => string;

/** The card's and the panel's title, from the artifact's own fields only. */
const TITLES: { readonly [K in WireArtifact['kind']]: Title<K> } = {
  load: (a) => `Load ${a.reference}`,
  timeline: (a) => `Load ${a.reference} · stops`,
  map: (a) => a.label,
  hos: (a) => a.driver,
  table: (a) => `${kindLabel(a.recordKind)} · ${a.rows.length}`,
  email: (a) => a.messages[0]?.subject ?? 'Email thread',
  doc: (a) => a.title,
  chart: (a) =>
    a.recordKind === undefined ? 'Records per day' : `${kindLabel(a.recordKind)} per day`,
  money: (a) =>
    a.lines.length === 1
      ? `Load ${a.lines[0]?.reference ?? ''} · rate`
      : `Rates · ${a.lines.length}`,
  board: (a) => `Load board · ${a.columns.length}`,
  customers: (a) => `Customers · ${a.rows.length}`,
  calendar: (a) => `${a.from} – ${a.to}`,
  ranking: (a) => (a.dimension === 'customer' ? 'Top customers' : 'Top trucks'),
  kpi: () => 'Headline numbers',
  chat: (a) => a.title ?? 'Chat thread',
  vehicle: (a) =>
    a.vehicles.length === 1 ? (a.vehicles[0]?.unit ?? 'Truck') : `Fleet · ${a.vehicles.length}`,
  driver: (a) =>
    a.drivers.length === 1 ? (a.drivers[0]?.name ?? 'Driver') : `Drivers · ${a.drivers.length}`,
};

export function artifactTitle(artifact: WireArtifact): string {
  return (TITLES[artifact.kind] as Title<WireArtifact['kind']>)(artifact);
}

/** Where the panel says it came from: the first citation, by its source's name. */
export function artifactSource(citations: readonly WireCitation[]): string {
  const first = citations[0];
  if (first === undefined) return 'sources';
  return first.sourceKind === undefined ? first.label : sourceKindName(first.sourceKind);
}

/** The latest retrieval behind the answer, for the panel's "retrieved …" footer. */
export function artifactRetrievedAt(citations: readonly WireCitation[]): string | undefined {
  return citations
    .map((c) => c.retrievedAt)
    .sort()
    .at(-1);
}

/** A Google Maps link for a point: the one outside URL the panel builds. */
export function mapsLink(lat: number, lon: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${lat.toFixed(6)},${lon.toFixed(6)}`;
}

/** "41.8781° N, 87.6298° W". */
export function coordinates(lat: number, lon: number): string {
  const ns = lat >= 0 ? 'N' : 'S';
  const ew = lon >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(4)}° ${ns}, ${Math.abs(lon).toFixed(4)}° ${ew}`;
}

/** "10h 35m" or "45m" from minutes left on an HOS clock. */
export function clockLeft(minutes: number | undefined): string {
  if (minutes === undefined) return '—';
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`;
}

/** The mono mark a stop wears in the calendar and the timeline. */
const STOP_MARKS = { pickup: 'PU', delivery: 'DE', stop: 'ST' } as const;

export function stopMark(kind: 'pickup' | 'delivery' | 'stop'): string {
  return STOP_MARKS[kind];
}

/** "Mon · Sep 21" — a calendar day's heading, read as UTC like the day itself. */
export function dayHeading(isoDay: string): string {
  return `${weekday(isoDay)} · ${shortDay(isoDay)}`;
}
